// iter690 — "My branch access" badge.
//
// Surfaces the operator's current branch scope at a glance:
//   • Home branch (from JWT / employee profile)
//   • Allowed branches (multi-branch staff)
//   • Currently logged-in branch (terminal context — for cashiers)
//
// Hidden entirely when the user has no branch restriction at all
// (admins / unscoped users) since the badge would just be noise.
//
// Designed to live in the desktop sidebar above the Logout button.
// Compact, scannable, with a popover that explains exactly what
// the user can / can't do given their current scope.

import { useEffect, useState } from 'react';
import { Building2, ChevronDown } from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { useAuthStore } from '@/stores';
import api from '@/lib/api';

export default function MyBranchAccessBadge() {
  const user = useAuthStore?.(s => s.user);
  const [branches, setBranches] = useState([]);

  // Fetch branch names ONCE so the popover can render "Branch A,
  // Branch B" instead of opaque IDs.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/branches');
        if (alive) setBranches(Array.isArray(data) ? data : []);
      } catch { /* sidebar badge is non-critical */ }
    })();
    return () => { alive = false; };
  }, []);

  // Resolve allowed branches: take the raw IDs from JWT and look them
  // up in the names cache. Falls back to the ID itself if unmapped.
  const homeBranchId = user?.branch_id || user?.home_branch_id || null;
  const allowedIds = Array.isArray(user?.allowed_branches) && user.allowed_branches.length
    ? user.allowed_branches
    : (homeBranchId ? [homeBranchId] : []);

  const idToName = Object.fromEntries((branches || []).map(b => [b.id, b.name]));
  const allowedNames = allowedIds.map(id => idToName[id] || id);
  const homeName = homeBranchId ? (idToName[homeBranchId] || homeBranchId) : null;
  // iter689 added `branch_name` on the login response — show that as
  // "currently working at" when it differs from home.
  const currentName = user?.branch_name || homeName;

  const role = (user?.role || 'staff').toLowerCase();
  const isPrivileged = ['admin', 'owner', 'super_admin'].includes(role);

  // Hide for fully-privileged users (no restriction → no useful info)
  // AND for users we know nothing about (logged out / pre-hydration).
  if (!user) return null;
  if (isPrivileged && allowedIds.length <= 1) return null;
  if (!allowedIds.length && !homeBranchId) return null;

  const isAway = homeBranchId && user?.branch_id && homeBranchId !== user.branch_id;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
            isAway
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100'
              : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100'
          }`}
          data-testid="my-branch-access-badge"
          title={`Role: ${role} · Allowed: ${allowedNames.length} branch(es)`}
        >
          <Building2 size={12} className="shrink-0" />
          <span className="flex-1 text-left truncate">
            {isAway ? `Away at ${currentName}` : (currentName || `${allowedNames.length} branch(es)`)}
          </span>
          <ChevronDown size={11} className="opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 p-3 text-[12px]" data-testid="my-branch-access-popover">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Your access</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${
              isPrivileged ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-zinc-50 text-zinc-700 border-zinc-200'
            }`}>{role}</span>
          </div>

          <div>
            <div className="text-[10px] text-muted-foreground">Home branch</div>
            <div className="font-semibold text-stone-800 dark:text-stone-200">{homeName || <em className="text-muted-foreground">none</em>}</div>
          </div>

          {isAway && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5">
              <div className="text-[10px] text-amber-800 font-semibold">⚠ Currently working at</div>
              <div className="font-semibold text-amber-900">{currentName}</div>
              <div className="text-[10px] text-amber-700 mt-0.5">All sales today recorded under this branch.</div>
            </div>
          )}

          <div>
            <div className="text-[10px] text-muted-foreground">Allowed branches ({allowedNames.length})</div>
            <ul className="mt-0.5 max-h-32 overflow-y-auto space-y-0.5">
              {allowedNames.length === 0
                ? <li className="text-muted-foreground italic">No restriction</li>
                : allowedNames.map((n, i) => (
                    <li key={i} className="text-stone-700 dark:text-stone-300 truncate" title={n}>
                      • {n}{n === currentName ? ' (current)' : ''}
                    </li>
                  ))}
            </ul>
          </div>

          <div className="pt-1.5 border-t border-stone-200 text-[10px] text-muted-foreground leading-snug">
            You can only view & write data for the branches above. Server-side enforcement prevents cross-branch leakage even if a stale browser tab thinks otherwise.
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
